import React from 'react';
import * as Styled from './MListIconDetail.styles';
import { type MListIconDetailProps, MListIconDetailSize } from './MListIconDetail.types';
import { AIcon } from '../../atoms/AIcon';
import { type AIconProps, AIconSize } from '../../atoms/AIcon/AIcon.types';
import { ATextInteractive } from '../../atoms/ATextInteractive';
import { type ATextInteractiveProps, ATextInteractiveSize } from '../../atoms/ATextInteractive/ATextInteractive.types';
import { ABody } from '../../atoms/ABody';
import { type ABodyProps, ABodySize, ABodyColor } from '../../atoms/ABody/ABody.types';
import { isFlagTrue, isFlagFalse } from '../../utils/aemFlagUtils';

const COMPONENT_NAME = 'm-web-ui-kit-MListIconDetail';
const TEST_ID = 'web-ui-kit-MListIconDetail';

type SizeConfig = {
  aIconSize: AIconSize;
  aIcon2Size: AIconSize;
  aTextInteractiveSize: ATextInteractiveSize;
  aBodySize: ABodySize;
};

const SIZE_MAP: Record<MListIconDetailSize, SizeConfig> = {
  [MListIconDetailSize.Small]: {
    aIconSize: AIconSize.L,
    aIcon2Size: AIconSize.XS,
    aTextInteractiveSize: ATextInteractiveSize.XS,
    aBodySize: ABodySize.XS,
  },
  [MListIconDetailSize.Medium]: {
    aIconSize: AIconSize.L,
    aIcon2Size: AIconSize.S,
    aTextInteractiveSize: ATextInteractiveSize.M,
    aBodySize: ABodySize.S,
  },
  [MListIconDetailSize.Large]: {
    aIconSize: AIconSize.L,
    aIcon2Size: AIconSize.M,
    aTextInteractiveSize: ATextInteractiveSize.M,
    aBodySize: ABodySize.M,
  },
};

function MListIconDetail({
  size = MListIconDetailSize.Medium,
  aIcon = {},
  aIcon2 = {},
  aTextInteractive,
  aBody,
  trailingIcon,
  ...rest
}: MListIconDetailProps) {
  const sizeMap = SIZE_MAP[size];
  const _aIcon = { size: sizeMap.aIconSize, ...aIcon } as AIconProps;
  const _aIcon2 = { size: sizeMap.aIcon2Size, ...aIcon2 } as AIconProps;
  const _aTextInteractive = aTextInteractive && { ...aTextInteractive, size: sizeMap.aTextInteractiveSize, ctaLink: isFlagFalse(aTextInteractive.ctaLink) ? undefined : aTextInteractive.ctaLink };
  const _aBody = aBody ? { color: ABodyColor.Alternate, size: sizeMap.aBodySize, ...aBody } : null;
  const showTrailingIcon = isFlagTrue(trailingIcon);

  return (
    <Styled.Wrapper data-component-name={COMPONENT_NAME} data-testid={TEST_ID} {...rest}>
      <Styled.ContentWrapper>
        {_aIcon.icon && <AIcon {...(_aIcon as AIconProps)} />}
        <Styled.TextWrapper>
          {_aTextInteractive?.text && (
            <Styled.TrailingIconTextWrapper>
              <ATextInteractive {...(_aTextInteractive as ATextInteractiveProps)} />
              {showTrailingIcon && (
                <AIcon
                  {...(_aIcon2 as AIconProps)}
                  icon={_aIcon2.icon ?? 'icon-success'}
                  size={_aIcon2.size ?? AIconSize.S}
                />
              )}
            </Styled.TrailingIconTextWrapper>
          )}
          {_aBody?.bodyText && <ABody {...(_aBody as ABodyProps)} />}
        </Styled.TextWrapper>
      </Styled.ContentWrapper>
    </Styled.Wrapper>
  );
}

export { MListIconDetail, MListIconDetailSize, TEST_ID as M_LIST_ICON_DETAIL_TEST_ID };
